/***************************************************************************************
 * FileName      : LearningProgressService.ts
 * Description   : 비회원 학습현황 localStorage 저장/조회 서비스 (싱글톤)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.09
 * Modified Date : 2026.02.12 시나리오 카드 라벨을 '시뮬레이션'으로 변경
 * Reference     : mockup/index.html (equipmentData.cards), mockup/asset/js/main.js
 ***************************************************************************************/

// 2026.02.09 : 박은정 : 학습카드 상태 타입 정의
export type CardStatus = 'pending' | 'in-progress' | 'completed';

// 2026.02.09 : 박은정 : 개별 학습카드 저장 데이터 타입
export interface CardProgress {
  status: CardStatus;
  completedAt?: string; // ISO 날짜 (완료 시각)
}

// 2026.02.09 : 박은정 : 장비별 학습 진행 데이터 타입
export interface EquipmentProgress {
  cards: Record<string, CardProgress>;
  evaluationScore?: number;  // 평가 점수 (0~100)
  lastCardId?: string;       // 마지막 학습 카드 ID
}

// 2026.02.09 : 박은정 : 전체 학습현황 저장 구조 타입
export interface LearningProgress {
  version: number;           // 스키마 버전 (마이그레이션용)
  lastUpdated: string;       // ISO 날짜
  equipment: Record<string, EquipmentProgress>;
}

// 2026.02.09 : 박은정 : 학습카드 정의 (이름 + 표시 라벨)
export interface CardDefinition {
  id: string;
  label: string;
}

// 2026.02.09 : 박은정 : 장비별 학습카드 목록
// 2026.05.14 : 박은정 : '구성' + '데이터 모니터링' 카드 통합 → '구성요소' (id: components, 5개 카드)
//   - 구성요소 카드: 부품 목록 + 모니터링 데이터(제어/센서) + 클릭 시 3D 박스 + 3D 오버레이 값 표시
export const EQUIPMENT_CARDS: Record<string, CardDefinition[]> = {
  'heat-exchanger': [
    { id: 'overview', label: '개요' },
    { id: 'process', label: '공정' },
    { id: 'components', label: '구성요소' },
    { id: 'scenario', label: '시뮬레이션' },
    { id: 'evaluation', label: '평가' },
  ],
  'distillation': [
    { id: 'overview', label: '개요' },
    { id: 'process', label: '공정' },
    { id: 'components', label: '구성요소' },
    { id: 'scenario', label: '시뮬레이션' },
    { id: 'evaluation', label: '평가' },
  ],
  'circulation': [
    { id: 'overview', label: '개요' },
    { id: 'process', label: '공정' },
    { id: 'components', label: '구성요소' },
    { id: 'scenario', label: '시뮬레이션' },
    { id: 'evaluation', label: '평가' },
  ],
};

// 2026.02.09 : 박은정 : localStorage 키
const STORAGE_KEY = 'scjeil-smart_ict_lab-app-learning-progress';

// 2026.02.09 : 박은정 : 현재 스키마 버전
const CURRENT_VERSION = 1;

// 2026.02.09 : 박은정 : 학습현황 서비스 클래스 (싱글톤 패턴)
export class LearningProgressService {
  private static instance: LearningProgressService;
  private data: LearningProgress;
  private useLocalStorage: boolean;

  private constructor() {
    this.useLocalStorage = this.checkLocalStorageAvailable();
    this.data = this.load();
  }

  // 2026.02.09 : 박은정 : 싱글톤 인스턴스 반환
  static getInstance(): LearningProgressService {
    if (!LearningProgressService.instance) {
      LearningProgressService.instance = new LearningProgressService();
    }
    return LearningProgressService.instance;
  }

  // 2026.02.09 : 박은정 : localStorage 사용 가능 여부 확인 (시크릿 모드 등 폴백)
  private checkLocalStorageAvailable(): boolean {
    try {
      const testKey = '__pej_ls_test__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      return false;
    }
  }

  // 2026.02.09 : 박은정 : 초기 학습현황 데이터 생성 (모든 카드 pending 상태)
  private createInitialData(): LearningProgress {
    const equipment: Record<string, EquipmentProgress> = {};

    for (const equipmentId of Object.keys(EQUIPMENT_CARDS)) {
      const cards: Record<string, CardProgress> = {};
      for (const card of EQUIPMENT_CARDS[equipmentId]) {
        cards[card.id] = { status: 'pending' };
      }
      equipment[equipmentId] = { cards };
    }

    return {
      version: CURRENT_VERSION,
      lastUpdated: new Date().toISOString(),
      equipment,
    };
  }

  // 2026.02.09 : 박은정 : localStorage에서 학습현황 읽기 (없으면 초기값 생성)
  private load(): LearningProgress {
    if (!this.useLocalStorage) {
      return this.createInitialData();
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const initial = this.createInitialData();
        this.saveToStorage(initial);
        return initial;
      }

      const parsed: LearningProgress = JSON.parse(raw);

      // 버전 불일치 시 마이그레이션 (현재는 v1만 존재하므로 초기화)
      if (parsed.version !== CURRENT_VERSION) {
        const initial = this.createInitialData();
        this.saveToStorage(initial);
        return initial;
      }

      // 새로 추가된 장비/카드가 있으면 병합
      for (const equipmentId of Object.keys(EQUIPMENT_CARDS)) {
        if (!parsed.equipment[equipmentId]) {
          const cards: Record<string, CardProgress> = {};
          for (const card of EQUIPMENT_CARDS[equipmentId]) {
            cards[card.id] = { status: 'pending' };
          }
          parsed.equipment[equipmentId] = { cards };
        } else {
          for (const card of EQUIPMENT_CARDS[equipmentId]) {
            if (!parsed.equipment[equipmentId].cards[card.id]) {
              parsed.equipment[equipmentId].cards[card.id] = { status: 'pending' };
            }
          }
        }
      }

      return parsed;
    } catch (error) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      const initial = this.createInitialData();
      this.saveToStorage(initial);
      return initial;
    }
  }

  // 2026.02.09 : 박은정 : localStorage에 학습현황 저장
  private saveToStorage(data: LearningProgress): void {
    if (!this.useLocalStorage) return;
    try {
      data.lastUpdated = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
    }
  }

  // 2026.02.09 : 박은정 : 현재 데이터를 localStorage에 동기화
  private save(): void {
    this.saveToStorage(this.data);
  }

  // 2026.02.09 : 박은정 : 전체 학습현황 데이터 반환 (읽기 전용 복사본)
  getData(): LearningProgress {
    return JSON.parse(JSON.stringify(this.data));
  }

  // 2026.02.09 : 박은정 : 장비별 진행률(%) 계산 (완료 카드 수 / 전체 카드 수 * 100)
  getEquipmentProgress(equipmentId: string): number {
    const eq = this.data.equipment[equipmentId];
    if (!eq) return 0;

    const cardDefs = EQUIPMENT_CARDS[equipmentId];
    if (!cardDefs || cardDefs.length === 0) return 0;

    const completedCount = cardDefs.filter(
      (card) => eq.cards[card.id]?.status === 'completed'
    ).length;

    return Math.round((completedCount / cardDefs.length) * 100);
  }

  // 2026.02.09 : 박은정 : 모든 장비의 진행률 맵 반환
  getAllProgress(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const equipmentId of Object.keys(EQUIPMENT_CARDS)) {
      result[equipmentId] = this.getEquipmentProgress(equipmentId);
    }
    return result;
  }

  // 2026.02.09 : 박은정 : 개별 카드 상태 조회
  getCardStatus(equipmentId: string, cardId: string): CardStatus {
    return this.data.equipment[equipmentId]?.cards[cardId]?.status || 'pending';
  }

  // 2026.02.09 : 박은정 : 장비의 모든 카드 상태 조회
  getCardStatuses(equipmentId: string): Record<string, CardProgress> {
    const eq = this.data.equipment[equipmentId];
    if (!eq) return {};
    return JSON.parse(JSON.stringify(eq.cards));
  }

  // 2026.02.09 : 박은정 : 카드 완료 처리
  markCardCompleted(equipmentId: string, cardId: string): void {
    const eq = this.data.equipment[equipmentId];
    if (!eq || !eq.cards[cardId]) return;

    eq.cards[cardId] = {
      status: 'completed',
      completedAt: new Date().toISOString(),
    };
    this.save();
  }

  // 2026.02.09 : 박은정 : 현재 학습 카드 설정 (in-progress 상태로 변경)
  // 2026.02.09 : 박은정 : 완료된 카드를 다시 학습할 경우 완료 상태를 해제하고 in-progress로 전환
  setCurrentCard(equipmentId: string, cardId: string): void {
    const eq = this.data.equipment[equipmentId];
    if (!eq || !eq.cards[cardId]) return;

    // 기존 in-progress 카드를 pending으로 되돌리기
    for (const key of Object.keys(eq.cards)) {
      if (eq.cards[key].status === 'in-progress') {
        eq.cards[key].status = 'pending';
      }
    }

    // 완료된 카드라도 다시 학습 시 완료 해제 → in-progress로 전환
    eq.cards[cardId] = { status: 'in-progress' };

    eq.lastCardId = cardId;
    this.save();
  }

  // 2026.02.11 : 박은정 : 완료된 카드를 다시 열었을 때 진행도를 초기화 (in-progress로 전환)
  resetCardToInProgress(equipmentId: string, cardId: string): void {
    const eq = this.data.equipment[equipmentId];
    if (!eq || !eq.cards[cardId]) return;

    eq.cards[cardId] = { status: 'in-progress' };
    eq.lastCardId = cardId;
    this.save();
  }

  // 2026.02.09 : 박은정 : 평가 점수 저장
  setEvaluationScore(equipmentId: string, score: number): void {
    const eq = this.data.equipment[equipmentId];
    if (!eq) return;

    eq.evaluationScore = Math.max(0, Math.min(100, score));
    this.save();
  }

  // 2026.02.09 : 박은정 : 장비의 마지막 학습 카드 ID 반환
  getLastCardId(equipmentId: string): string | undefined {
    return this.data.equipment[equipmentId]?.lastCardId;
  }

  // 2026.02.09 : 박은정 : 전체 학습현황 초기화
  resetAll(): void {
    this.data = this.createInitialData();
    this.save();
  }
}

